import { AxiosError, AxiosInstance } from 'axios';

export interface ApiFieldError {
    code: string;
    params?: Record<string, any>;
    /**
     * Backend-provided human-readable message for this specific field error. Already
     * locale-resolved server-side via the Accept-Language header. Preferred for direct
     * display when no client-side override is needed.
     */
    message?: string;
}

export interface ApiError {
    code: string;
    params?: Record<string, any>;
    fields?: Record<string, ApiFieldError[]>;
    /** Populated when we fell back to a legacy envelope. Use code for logic; this is for compatibility only. */
    legacyMessage?: string;
}

export interface AxiosErrorWithApiError extends AxiosError {
    apiError?: ApiError;
}

/**
 * Parse any shape the backend might currently return into our canonical ApiError.
 * During the migration, backend can emit either the new {error:{code,params}} envelope
 * or the legacy {message:'...'} / {error_login:'...'} shapes. This normalizer lets
 * frontend code read .apiError.code uniformly.
 */
export function parseApiError(err: any): ApiError {
    const data = err?.response?.data;

    if (data && typeof data === 'object' && data.error && typeof data.error.code === 'string') {
        return {
            code: data.error.code,
            params: data.error.params ?? {},
            fields: data.error.fields,
        };
    }

    if (data && typeof data.message === 'string') {
        return {
            code: 'legacy.message',
            params: { message: data.message },
            legacyMessage: data.message,
        };
    }

    if (data && typeof data.error_login === 'string') {
        return {
            code: 'auth.invalid_credentials',
            params: {},
            legacyMessage: data.error_login,
        };
    }

    if (data && data.errors && typeof data.errors === 'object') {
        const fields: Record<string, ApiFieldError[]> = {};
        for (const field in data.errors) {
            if (Object.prototype.hasOwnProperty.call(data.errors, field)) {
                const messages = Array.isArray(data.errors[field]) ? data.errors[field] : [data.errors[field]];
                fields[field] = messages.map((m: string) => ({
                    code: 'validation.field',
                    params: { message: m },
                }));
            }
        }
        return { code: 'validation.failed', params: {}, fields };
    }

    if (err?.message === 'Network Error' || !err?.response) {
        return { code: 'network.error', params: {} };
    }

    return { code: 'unknown.error', params: { status: err?.response?.status } };
}

export function installApiErrorInterceptor(client: AxiosInstance): void {
    client.interceptors.response.use(
        (response) => response,
        (error: AxiosErrorWithApiError) => {
            error.apiError = parseApiError(error);
            return Promise.reject(error);
        },
    );
}

/**
 * Convert a camelCase / snake_case field key into a "Title Cased" human label.
 * Used as the last-resort field name when no `fields.<key>` translation is present.
 *
 *   "passwordConfirmation" → "Password Confirmation"
 *   "first_name"           → "First Name"
 */
function humanizeFieldKey(field: string): string {
    return field
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/\s+/g, ' ')
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
}

/**
 * Render a parsed ApiError as a single user-facing string suitable for `toast.error`.
 *
 * Priority order:
 *   1. **Field validation errors** (`apiError.fields`) — for each offending field, prefer
 *      the active locale's translation of `errors.<field.code>` (e.g. `errors.validation.min.numeric`
 *      → "Duhet të jetë së paku {min}.") with `field.params` passed as ICU values. Falls
 *      back to the backend's `message` when no client-side translation key exists.
 *
 *      Each line is prefixed with the field name (`fields.<key>` translation if present,
 *      otherwise the humanized field key) so multi-field 422s like "Password: …, Email: …"
 *      remain comprehensible.
 *
 *   2. **Known top-level business code** — looks up `errors.<apiError.code>` in the active
 *      locale (e.g. `errors.company.user_limit_reached`) with `apiError.params` passed as
 *      ICU values. Convention: every business-error code has a matching translation key.
 *
 *   3. **Last resort** — `apiError.legacyMessage` if present, else `common.somethingWentWrong`.
 *
 * Designed for the component layer where `intl.formatMessage` is available via `useIntl()`
 * or the `injectIntl` HOC. Pass `intl.formatMessage` as the second argument.
 *
 * @example
 *   const intl = useIntl();
 *   try { await axiosCaller('POST', 'admin/users', payload); }
 *   catch (err: any) {
 *       toast.error(formatApiErrorMessage(err?.apiError, intl.formatMessage));
 *   }
 */
export function formatApiErrorMessage(
    apiError: ApiError | undefined,
    formatMessage: (descriptor: { id: string; defaultMessage: string }, values?: Record<string, any>) => string,
): string {
    const fallback = () => formatMessage({
        id: 'common.somethingWentWrong',
        defaultMessage: 'Something went wrong.',
    });

    if (!apiError) return fallback();

    // 1. Field-level validation errors — preferred path. Translate the validation `code`
    //    (e.g. validation.min.numeric) client-side using the active locale so the user
    //    sees their language even when the backend hardcodes English `message` strings.
    if (apiError.fields && Object.keys(apiError.fields).length > 0) {
        const lines: string[] = [];
        for (const field of Object.keys(apiError.fields)) {
            const errs = apiError.fields[field];
            if (!Array.isArray(errs) || errs.length === 0) continue;
            const first = errs[0];

            // Backend's English message (already localized server-side if backend honors
            // Accept-Language; otherwise English) — used as the defaultMessage so we never
            // show worse than the backend-provided text.
            const backendMessage = first?.message || first?.params?.message || '';

            // Translate via the field-level code. If the key isn't in the active locale,
            // formatMessage returns the defaultMessage (backend message) — strictly better
            // than nothing. The legacy normalizer (parseApiError) uses code='validation.field'
            // with params.message, which resolves through the existing
            // "errors.validation.field": "{message}" key, so legacy errors still localize.
            const codeKey = first?.code ? `errors.${first.code}` : null;
            const text = codeKey
                ? formatMessage(
                    { id: codeKey, defaultMessage: backendMessage },
                    first.params || {},
                )
                : backendMessage;

            if (!text) continue;

            // Prefix with a human label so multi-field errors stay readable. Try a
            // localised field label first (e.g. fields.password → "Fjalëkalimi"); fall
            // back to the humanized key.
            const fieldLabel = formatMessage(
                { id: `fields.${field}`, defaultMessage: humanizeFieldKey(field) },
            );
            lines.push(`${fieldLabel}: ${text}`);
        }
        if (lines.length > 0) return lines.join('\n');
    }

    // 2. Known top-level business error code. By convention every code maps to a
    //    matching `errors.<code>` i18n key. If no translation exists and there's no
    //    legacyMessage to fall back on, we drop through to the generic toast below.
    if (apiError.code && apiError.code !== 'unknown.error') {
        const formatted = formatMessage(
            { id: `errors.${apiError.code}`, defaultMessage: apiError.legacyMessage || '' },
            apiError.params || {},
        );
        if (formatted && formatted.trim()) return formatted;
    }

    // 3. Last resort.
    return apiError.legacyMessage || fallback();
}

/**
 * Detects the "my token just got revoked mid-session" case: a 401 comes back on a
 * request that HAD a tenant bearer token, and that request wasn't part of the
 * auth flow itself. Clears local auth state, toasts an informational message,
 * and bounces to /login so the user sees a real page instead of staring at
 * blank screens while every API call fails silently.
 *
 * Does NOT fire for:
 *   - /login, /forgot-password, /reset-password routes (normal auth errors belong
 *     to those forms to handle inline)
 *   - /super-admin/* routes (superAdminAxiosCaller handles its own 401 path)
 *   - Users who weren't logged in to begin with (no mikaBearerToken in storage)
 *
 * Hook this via installSessionExpiredInterceptor(axios, onExpired) from index.tsx
 * so the side-effect (toast + redirect) is injected from the component tree where
 * react-toastify is available.
 */
export type OnSessionExpired = () => void;

/**
 * Forwards the user's current UI locale to the backend via the standard
 * Accept-Language header, so Laravel's SetLocale middleware picks it up and
 * app()->setLocale() resolves for server-generated output (PDFs, emails, etc).
 * Falls back to the i18n DEFAULT_LOCALE when storage is unreadable.
 */
export function installAcceptLanguageInterceptor(
    client: AxiosInstance,
    getLocale: () => string,
): void {
    client.interceptors.request.use((config) => {
        try {
            const locale = getLocale();
            if (locale) {
                config.headers = config.headers || {};
                (config.headers as any)['Accept-Language'] = locale;
            }
        } catch { /* swallow — locale is a nice-to-have, never block the request */ }
        return config;
    });
}

export function installSessionExpiredInterceptor(
    client: AxiosInstance,
    onExpired: OnSessionExpired,
): void {
    client.interceptors.response.use(
        (response) => response,
        (error: AxiosErrorWithApiError) => {
            const status = error?.response?.status;
            const url = (error?.config?.url || '').toString();

            const hadTenantToken = !!(() => {
                try { return localStorage.getItem('mikaBearerToken'); } catch { return null; }
            })();

            const isAuthRoute =
                url.includes('/login') ||
                url.includes('/forgot-password') ||
                url.includes('/reset-password') ||
                url.includes('/authenticated');
            const isSuperAdminRoute = url.includes('/super-admin/');
            const isImpersonationRoute = url.includes('/impersonation/');

            // During an active impersonation, 401s are the banner's job to
            // handle — it has its own cleanup (clear sessionStorage, try to
            // close the tab). Firing the generic session-ended toast here
            // would race with that and bounce the user to /login mid-cleanup.
            const hasImpersonationToken = !!(() => {
                try { return sessionStorage.getItem('imp_access_token'); } catch { return null; }
            })();

            if (
                status === 401
                && hadTenantToken
                && !isAuthRoute
                && !isSuperAdminRoute
                && !isImpersonationRoute
                && !hasImpersonationToken
            ) {
                try { onExpired(); } catch { /* swallow — don't mask the original error */ }
            }

            return Promise.reject(error);
        },
    );
}
